import qs from 'qs';
import { get } from 'lodash';

import apiCore, { withAuth } from './core';
import lookerApi from './looker-api';

const TVT_API_ROOT = process.env.REACT_APP_TV_TIME_API;
const PORTAL_API_ROOT = process.env.REACT_APP_PORTAL_API;

export default {
  /**
   *
   * @param {string} username
   * @param {string} password
   *
   */
  login: async (username, password) => {
    const url = `${PORTAL_API_ROOT}v1/login`;
    const body = qs.stringify({ username, password });
    const overrides = {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    };

    try {
      const { data } = await apiCore.post(url, body, overrides);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  logout: async () => {
    try {
      await withAuth(apiCore.get)(`${PORTAL_API_ROOT}v1/logout`);
      return null;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  forgotPassword: async username => {
    const url = `${PORTAL_API_ROOT}v1/forgot-password`;
    const body = qs.stringify({ username, host: window.location.origin });
    const overrides = {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    };

    try {
      const { data } = await apiCore.post(url, body, overrides);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  resetPassword: async (hash, username, password) => {
    const url = `${PORTAL_API_ROOT}v1/reset-password`;
    const body = qs.stringify({ hash, username, password });
    const overrides = {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    };

    try {
      const { data } = await apiCore.post(url, body, overrides);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  getAllUserSegments: async () => {
    try {
      const url = `${PORTAL_API_ROOT}v1/audience-segment`;
      const { data } = await withAuth(apiCore.get)(url);

      return data;
    } catch (err) {
      console.log(err);
      Promise.reject(err);
    }
  },

  getSegment: async uuid => {
    const url = `${PORTAL_API_ROOT}v1/audience-segment/${uuid}`;

    try {
      const { data } = await withAuth(apiCore.get)(url);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  checkHealthStatus: async () => {
    const url = `${PORTAL_API_ROOT}v1/status-check`;

    try {
      const { data } = await withAuth(apiCore.get)(url);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  getFiltersConfig: async () => {
    try {
      const url = `${PORTAL_API_ROOT}v1/user/filters_permissible`;

      const { data } = await withAuth(apiCore.get)(url);
      return data.filters;
    } catch (err) {
      console.log(err);
      return Promise.reject(err);
    }
  },

  /**
   *
   * @param {number} showId
   *
   */
  getShowData: async showId => {
    const showInfoQuery = {
      id: 'seriesDetail',
      meta: {
        fields: ['show', 'contentType']
      }
    };

    try {
      const result = await lookerApi.runInlineQueryStream(
        showInfoQuery.id,
        showInfoQuery.meta,
        {
          contentType: {
            id: 'contentType',
            value: { value: 'series' }
          },
          show: {
            id: 'show',
            value: { value: showId }
          }
        },
        'json'
      );
      const showJson = get(result, ['data', 0], {});
      return showJson;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   *
   * @param {number} showId
   * @param {number} showId
   *
   */
  getShowComments: async (showId, { limit = 10, page = 0, lang = '' } = {}) => {
    const query = qs.stringify({ limit, page, lang });
    const url = `${TVT_API_ROOT}show/${showId}/comments?${query}`;

    const response = await fetch(url);
    return response.json();
  },

  getEpisodeComments: async (
    showId,
    episodeId,
    { limit = 10, page = 0, lang = '' } = {}
  ) => {
    const query = qs.stringify({ limit, page, lang });
    const url = `${TVT_API_ROOT}show/${showId}/episode/${episodeId}/comments?${query}`;

    const response = await fetch(url);
    return response.json();
  },

  /**
   * @param {number} showId
   * @param {number} episodeId
   */
  getEpisodeData: (showId, episodeId) =>
    new Promise((resolve, reject) => {
      const queryParams = {
        include_all_affiliations: true,
        country: 'US'
      };

      fetch(
        `${TVT_API_ROOT}show/${showId}/episode/${episodeId}/data/en?${qs.stringify(
          queryParams
        )}`
      ).then(
        response => resolve(response.json()),
        error => reject(error)
      );
    }),

  /**
   * @param {number} actorId
   */
  getActorData: async actorId => {
    const actorInfoQuery = {
      id: 'actorDetail',
      meta: {
        fields: ['actor']
      }
    };

    try {
      const result = await lookerApi.runInlineQueryStream(
        actorInfoQuery.id,
        actorInfoQuery.meta,
        {
          actor: {
            id: 'actor',
            value: { value: actorId }
          }
        },
        'json'
      );
      const actorJson = get(result, ['data', 0], {});
      return actorJson;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // TODO, we'll need an endpoint to get simple network data: e.g. name, thumbnail, list of show IDs that exist on that network.
  getNetworkData: networkId => this.getShowData(networkId),

  saveAudienceSegment: async (
    uuid = '',
    name,
    filters,
    options = { isBenchmark: false, isDefault: false }
  ) => {
    const url = `${PORTAL_API_ROOT}v1/audience-segment/${uuid}`;
    const body = JSON.stringify({
      name,
      filters,
      options
    });

    try {
      const { data } = await withAuth(apiCore.put)(url, body);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  deleteAudienceSegment: async (uuid = '') => {
    const url = `${PORTAL_API_ROOT}v1/audience-segment/${uuid}`;

    try {
      const { data } = await withAuth(apiCore.del)(url);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  updateUsername: async (uuid = '', username) => {
    const url = `${PORTAL_API_ROOT}v1/user/${uuid}`;
    const body = JSON.stringify({
      username
    });

    try {
      const { data } = await withAuth(apiCore.put)(url, body);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  },
  getMovieData: async (movieId) => {
    try {
      const result = await lookerApi.runInlineQuery(
        'movieDetail',
        {
          fields: ['movie']
        },
        {
          movie: {
            id: 'movie',
            label: 'Movie',
            isMulti: true,
            value: {
              value: movieId
            }
          }
        },
        'json'
      );

      const movieJson = get(result, ['data', 0], {});
      return movieJson;
    } catch (err) {
      return Promise.reject((err));
    }
  }
};
